<template>
    <main class="careers-page">
        <HeaderSection />

        <!-- HERO -->
        <section class="careers-hero">
            <div class="hero-background" :style="{ backgroundImage: `url('${careerImage('career-hero.jpg')}')` }"></div>
            <div class="hero-overlay"></div>

            <div class="hero-content">
                <span class="hero-kicker">CAREERS AT UNITeCORE</span>
                <h1>
                    Build your career.<br />
                    <em>Build what's next.</em>
                </h1>
                <p>
                    Join a team where technology, ideas and people come together
                    to create meaningful impact.
                </p>

            </div>

            <div class="hero-scroll">
                <span>SCROLL TO EXPLORE</span>
                <i class="bi bi-arrow-down"></i>
            </div>
        </section>

        <!-- LIFE AT UNITeCORE -->
        <section class="life-section">
            <div class="section-wrap">
                <div class="section-heading">
                    <div class="section-kicker">01 / LIFE AT UNITeCORE</div>
                    <h2>
                        Work with purpose.<br />
                        <em>Grow with people.</em>
                    </h2>
                    <p>
                        At UniteCore, we believe a great workplace is created by people
                        who support one another, share ideas and enjoy building something
                        meaningful together.
                    </p>
                </div>

                <div class="life-gallery">
                    <div class="gallery-item gallery-large">
                        <img :src="careerImage('career-team-1.jpg')" alt="UniteCore team working together" />
                        <span>People first</span>
                    </div>
                    <div class="gallery-item">
                        <img :src="careerImage('career-team-2.jpg')" alt="Professionals collaborating" loading="lazy" />
                        <span>Collaborate</span>
                    </div>
                    <div class="gallery-item">
                        <img :src="careerImage('career-team-3.jpg')" alt="Professionals working in a modern office"
                            loading="lazy" />
                        <span>Create</span>
                    </div>
                    <div class="gallery-item gallery-wide">
                        <img :src="careerImage('career-office.jpg')" alt="Modern workplace" loading="lazy" />
                        <span>One team</span>
                    </div>
                    <div class="gallery-item">
                        <img :src="careerImage('career-team-4.jpg')" alt="Team discussion in office" loading="lazy" />
                        <span>Connect</span>
                    </div>
                    <div class="gallery-item">
                        <img :src="careerImage('career-team-5.jpg')" alt="Colleagues at work" loading="lazy" />
                        <span>Grow</span>
                    </div>
                </div>
            </div>
        </section>

        <!-- CORE VALUES -->
        <section class="core-values-section">
            <div class="section-wrap">
                <div class="core-values-intro">
                    <div class="section-kicker">02 / OUR CORE VALUES</div>
                    <h2>
                        What we believe.<br />
                        <em>How we work.</em>
                    </h2>
                    <p>
                        Our values shape the way we work, collaborate and grow. They guide
                        our decisions, strengthen our culture and help us create a workplace
                        where every individual can make a difference.
                    </p>
                </div>

                <div class="core-values-grid">
                    <article v-for="(value, index) in coreValues" :key="value.title" class="core-value-card">
                        <div class="core-value-image">
                            <img :src="value.image" :alt="value.alt" loading="lazy" />
                            <span class="core-value-number">
                                {{ String(index + 1).padStart(2, '0') }}
                            </span>
                        </div>
                        <div class="core-value-content">
                            <div class="core-value-icon">
                                <i :class="value.icon"></i>
                            </div>
                            <h3>{{ value.title }}</h3>
                            <p>{{ value.description }}</p>
                        </div>
                    </article>
                </div>
            </div>
        </section>

        <!-- CAREER VIDEO -->
        <section class="career-video-section">
            <div class="section-wrap">
                <div class="career-video-heading">
                    <div class="section-kicker">03 / OUR CULTURE</div>
                    <h2>
                        More than a workplace.<br />
                        <em>A place to belong.</em>
                    </h2>
                </div>

                <div class="career-video">
                    <video :key="careerVideoUrl" controls playsinline preload="metadata" @error="videoHasError = true"
                        @loadeddata="videoHasError = false">
                        <source :src="careerVideoUrl" type="video/mp4" />
                        Your browser does not support HTML video.
                    </video>

                    <div v-if="videoHasError" class="video-error">
                        <i class="bi bi-exclamation-circle"></i>
                        <h3>Unable to load the career video</h3>
                        <p>
                            Please verify that Career-Page.mp4 exists in
                            the public/assets folder and that the file is a valid MP4.
                        </p>
                        <a :href="careerVideoUrl" target="_blank" rel="noopener noreferrer">
                            Open video file
                            <i class="bi bi-arrow-up-right"></i>
                        </a>
                    </div>
                </div>
            </div>
        </section>



        <!-- CURRENT OPENINGS (COMMENTED OUT) -->
        <!--
        <section class="openings-section" id="openings">
            <div class="section-wrap">
                <div class="section-heading openings-heading">
                    <div class="section-kicker">04 / OPEN POSITIONS</div>
                    <h2>
                        Find your next<br />
                        <em>opportunity.</em>
                    </h2>
                    <p>
                        Explore opportunities at UniteCore and take the next step in your
                        professional journey.
                    </p>
                </div>

                <div class="openings-list">
                    <article v-for="job in jobs" :key="job.title" class="opening-card">
                        <div class="opening-info">
                            <span class="opening-type">{{ job.type }}</span>
                            <h3>{{ job.title }}</h3>
                            <p>{{ job.description }}</p>
                        </div>

                        <div class="opening-meta">
                            <span>
                                <i class="bi bi-geo-alt"></i>
                                {{ job.location }}
                            </span>
                            <span>
                                <i class="bi bi-briefcase"></i>
                                {{ job.department }}
                            </span>
                        </div>
                    </article>
                </div>
            </div>
        </section>
        -->

        <!-- APPLICATION -->
        <section class="application-section" id="application">
            <div class="section-wrap">
                <div class="application-card">
                    <div class="application-copy">
                        <div class="section-kicker">05 / JOIN UNITeCORE</div>
                        <h2>
                            Don't see the<br />
                            <em>right role?</em>
                        </h2>
                        <p>
                            We are always interested in meeting talented and motivated
                            people. Share your profile with us and tell us how you can
                            contribute to the UniteCore journey.
                        </p>
                        <div class="application-note">
                            <i class="bi bi-arrow-right"></i>
                            <span>
                                Send us your profile and our team will get in touch when a
                                suitable opportunity becomes available.
                            </span>
                        </div>
                    </div>

                    <form class="career-form" @submit.prevent="submitApplication">
                        <div class="form-row">
                            <div class="form-group">
                                <label for="career-name">Full Name</label>
                                <input id="career-name" v-model.trim="form.name" type="text"
                                    placeholder="Enter your full name" autocomplete="name" required />
                            </div>
                            <div class="form-group">
                                <label for="career-email">Email Address</label>
                                <input id="career-email" v-model.trim="form.email" type="email"
                                    placeholder="Enter your email address" autocomplete="email" required />
                            </div>
                        </div>

                        <div class="form-row">
                            <div class="form-group">
                                <label for="career-phone">Phone Number</label>
                                <input id="career-phone" v-model.trim="form.phone" type="tel"
                                    placeholder="Enter your phone number" autocomplete="tel" required />
                            </div>
                            <div class="form-group">
                                <label for="career-role">Interested Role</label>
                                <select id="career-role" v-model="form.role" required>
                                    <option value="">Select a role</option>
                                    <option value="frontend-developer">Frontend Developer</option>
                                    <option value="business-development">Business Development Executive</option>
                                    <option value="it-support">IT Support Engineer</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>
                        </div>

                        <div class="form-group">
                            <label for="career-message">Message</label>
                            <textarea id="career-message" v-model.trim="form.message" rows="5"
                                placeholder="Tell us a little about yourself..."></textarea>
                        </div>

                        <div class="form-group">
                            <label for="career-resume">Resume</label>
                            <div class="resume-input">
                                <input id="career-resume" type="file" accept=".pdf,.doc,.docx" required
                                    @change="handleResume" />
                                <i class="bi bi-cloud-arrow-up"></i>
                                <span>
                                    {{ resumeName || 'Upload your resume' }}
                                    <small>PDF, DOC or DOCX (max 10 MB)</small>
                                </span>
                            </div>
                        </div>

                        <p v-if="formMessage" class="form-message" role="status">
                            {{ formMessage }}
                        </p>

                        <button type="submit" class="career-submit">
                            Prepare Application
                            <i class="bi bi-arrow-right"></i>
                        </button>
                        <p class="form-disclaimer">
                            This form prepares an email application. It does not upload
                            files to a server automatically.
                        </p>
                    </form>
                </div>
            </div>
        </section>

        <FooterSection />
    </main>
</template>

<script>
import HeaderSection from "../components/HeaderSection.vue";
import FooterSection from "../components/FooterSection.vue";

export default {
    name: "CareersPage",

    components: {
        HeaderSection,
        FooterSection,
    },

    data() {
        const asset = (name) => {
            const base = process.env.BASE_URL || "/";
            return `${base}assets/${name}`;
        };

        return {
            videoHasError: false,
            resumeFile: null,
            resumeName: "",
            formMessage: "",
            careerVideoUrl: this.publicAsset("videos/Career-Page.mp4"),

            coreValues: [
                {
                    title: "Integrity & Trust",
                    icon: "bi bi-shield-check",
                    image: "https://images.pexels.com/photos/4308105/pexels-photo-4308105.jpeg?auto=compress&cs=tinysrgb&w=1200",
                    alt: "Professionals discussing work",
                    description: "We act with honesty, keep our commitments and build relationships based on trust, transparency and respect.",
                },
                {
                    title: "Collaboration",
                    icon: "bi bi-people",
                    image: asset("collaboration.jpg"),
                    alt: "Colleagues collaborating around a laptop",
                    description: "We share ideas, listen to different perspectives and work together to achieve better outcomes as one team.",
                },
                {
                    title: "Innovation",
                    icon: "bi bi-lightbulb",
                    image: "https://images.pexels.com/photos/7793688/pexels-photo-7793688.jpeg?auto=compress&cs=tinysrgb&w=1200",
                    alt: "Professionals brainstorming",
                    description: "We stay curious, challenge assumptions and turn better ideas into practical solutions that create real value.",
                },
                {
                    title: "Ownership",
                    icon: "bi bi-person-check",
                    image: "https://images.pexels.com/photos/31786661/pexels-photo-31786661.jpeg?auto=compress&cs=tinysrgb&w=1200",
                    alt: "Professionals planning a project",
                    description: "We take responsibility for our work, follow through on our commitments and take pride in the impact we create.",
                },
                {
                    title: "Growth & Learning",
                    icon: "bi bi-graph-up-arrow",
                    image: asset("growth.jpg"),
                    alt: "Colleagues learning together",
                    description: "We keep learning, mentor one another and create opportunities for people to develop their skills and careers.",
                },
                {
                    title: "Respect & Inclusion",
                    icon: "bi bi-heart",
                    image: asset("inclusion.jpg"),
                    alt: "Colleagues working together",
                    description: "We value every voice and create an environment where people feel respected, supported and confident to contribute.",
                },
            ],

            benefits: [
                { title: "Career Growth", icon: "bi bi-graph-up-arrow", description: "Opportunities to learn, take ownership and grow into bigger responsibilities." },
                { title: "Learning & Development", icon: "bi bi-mortarboard", description: "Continuous learning through hands-on experience, knowledge sharing and mentoring." },
                { title: "Collaborative Culture", icon: "bi bi-people", description: "Work alongside talented people who believe in teamwork and knowledge sharing." },
                { title: "Meaningful Work", icon: "bi bi-lightning-charge", description: "Work on technology solutions that solve real business challenges for customers." },
                { title: "Work Environment", icon: "bi bi-person-workspace", description: "A professional and supportive environment where ideas and initiative are encouraged." },
                { title: "Recognition", icon: "bi bi-award", description: "We appreciate meaningful contributions and celebrate individual and team achievements." },
                { title: "Open Communication", icon: "bi bi-chat-square-heart", description: "An environment where employees can share ideas, feedback and perspectives openly." },
                { title: "Technology Exposure", icon: "bi bi-globe2", description: "Exposure to modern technologies, platforms and diverse enterprise requirements." },
                { title: "Innovation", icon: "bi bi-stars", description: "Space to experiment, solve problems creatively and contribute new ideas." },
                { title: "People First", icon: "bi bi-house-heart", description: "We believe strong teams are built through respect, inclusion and genuine support." },
            ],

            jobs: [
                {
                    title: "Frontend Developer",
                    value: "frontend-developer",
                    type: "FULL TIME",
                    location: "Pune",
                    department: "Engineering",
                    description: "Build modern, responsive and high-performance web experiences using contemporary frontend technologies.",
                },
                {
                    title: "Business Development Executive",
                    value: "business-development",
                    type: "FULL TIME",
                    location: "Pune",
                    department: "Business Development",
                    description: "Build strong customer relationships and identify opportunities to create long-term business value.",
                },
                {
                    title: "IT Support Engineer",
                    value: "it-support",
                    type: "FULL TIME",
                    location: "Pune",
                    department: "IT Operations",
                    description: "Support enterprise technology environments and help customers maintain secure and reliable IT operations.",
                },
            ],

            form: {
                name: "",
                email: "",
                phone: "",
                role: "",
                message: "",
            },
        };
    },

    methods: {
        publicAsset(fileName) {
            const baseUrl = process.env.BASE_URL || "/";
            const cleanFileName = String(fileName).replace(/^\/+/, "");

            return `${baseUrl}${cleanFileName}`;
        },

        careerImage(fileName) {
            const externalImages = {
                "career-hero.jpg": "https://images.pexels.com/photos/6804068/pexels-photo-6804068.jpeg?auto=compress&cs=tinysrgb&w=1800",
                "career-team-1.jpg": "https://images.pexels.com/photos/7988742/pexels-photo-7988742.jpeg?auto=compress&cs=tinysrgb&w=1400",
                "career-team-2.jpg": "https://images.pexels.com/photos/7988079/pexels-photo-7988079.jpeg?auto=compress&cs=tinysrgb&w=1400",
                "career-team-3.jpg": "https://images.pexels.com/photos/6804083/pexels-photo-6804083.jpeg?auto=compress&cs=tinysrgb&w=1400",
                "career-office.jpg": "https://images.pexels.com/photos/12899167/pexels-photo-12899167.jpeg?auto=compress&cs=tinysrgb&w=1600",
                "career-team-4.jpg": "https://images.pexels.com/photos/7988747/pexels-photo-7988747.jpeg?auto=compress&cs=tinysrgb&w=1400",
                "career-team-5.jpg": "https://images.pexels.com/photos/12899153/pexels-photo-12899153.jpeg?auto=compress&cs=tinysrgb&w=1400",
            };

            return externalImages[fileName] || this.publicAsset(fileName);
        },

        selectRole(role) {
            this.form.role = role;
            this.formMessage = "";
        },

        handleResume(event) {
            const file = event.target.files && event.target.files[0];

            if (!file) {
                this.resumeFile = null;
                this.resumeName = "";
                return;
            }

            const allowedTypes = [
                "application/pdf",
                "application/msword",
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            ];

            const extensionAllowed = /\.(pdf|doc|docx)$/i.test(file.name);

            if (!extensionAllowed || (file.type && !allowedTypes.includes(file.type))) {
                this.formMessage = "Please choose a PDF, DOC or DOCX resume.";
                event.target.value = "";
                this.resumeFile = null;
                this.resumeName = "";
                return;
            }

            if (file.size > 10 * 1024 * 1024) {
                this.formMessage = "Your resume must be smaller than 10 MB.";
                event.target.value = "";
                this.resumeFile = null;
                this.resumeName = "";
                return;
            }

            this.resumeFile = file;
            this.resumeName = file.name;
            this.formMessage = "";
        },

        submitApplication() {
            if (!this.resumeFile) {
                this.formMessage = "Please upload your resume before continuing.";
                return;
            }

            const roleNames = {
                "frontend-developer": "Frontend Developer",
                "business-development": "Business Development Executive",
                "it-support": "IT Support Engineer",
                other: "Other",
            };

            const subject = encodeURIComponent(
                `Career Application - ${roleNames[this.form.role] || this.form.role}`
            );

            const body = encodeURIComponent(
                `Career Application\n\n` +
                `Name: ${this.form.name}\n` +
                `Email: ${this.form.email}\n` +
                `Phone: ${this.form.phone}\n` +
                `Interested Role: ${roleNames[this.form.role] || this.form.role}\n\n` +
                `Message:\n${this.form.message}\n\n` +
                `Resume file: ${this.resumeFile.name}\n\n` +
                `Please attach the resume file before sending this email.`
            );

            this.formMessage =
                "Your email application is being prepared. Attach your resume in your email app before sending.";

            window.location.href =
                `mailto:?subject=${subject}&body=${body}`;
        },
    },
};
</script>

<style scoped>
.careers-page {
    --green: #102e27;
    --green-dark: #0b2520;
    --green-deep: #071c18;
    --green-light: #edf4f1;
    --green-soft: #f5f8f6;
    --ink: #08090b;
    --white: #fff;
    --muted: #333835;
    --line: #dce5e1;
    --orange: #f88c18;
    --orange-light: #ffb15a;

    background: var(--white);
    color: var(--ink);
    overflow: hidden;
}

.careers-page *,
.careers-page *::before,
.careers-page *::after {
    box-sizing: border-box;
}

.section-wrap {
    width: min(1180px, calc(100% - 48px));
    margin: 0 auto;
}

.section-kicker {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    color: var(--orange);
    font-size: 13px;
    font-weight: 800;
    letter-spacing: .16em;
    text-transform: uppercase;
}

.section-heading {
    max-width: 720px;
}

.section-heading h2,
.core-values-intro h2,
.career-video-heading h2,
.application-copy h2 {
    margin: 16px 0 22px;
    font-size: clamp(42px, 5vw, 72px);
    line-height: .98;
    letter-spacing: -.045em;
    font-weight: 800;
}

.section-heading h2 em,
.core-values-intro h2 em,
.career-video-heading h2 em,
.application-copy h2 em {
    color: var(--green);
    font-style: normal;
}

.section-heading>p,
.core-values-intro>p {
    max-width: 650px;
    margin: 0;
    color: var(--muted);
    font-size: 17px;
    line-height: 1.8;
}

/* HERO */
.careers-hero {
    position: relative;
    min-height: 720px;
    display: flex;
    align-items: center;
    isolation: isolate;
    background: var(--green-deep);
    overflow: hidden;
}

.hero-background {
    position: absolute;
    inset: 0;
    z-index: -3;
    background-position: center;
    background-size: cover;
}

.hero-overlay {
    position: absolute;
    inset: 0;
    z-index: -2;
    background:
        linear-gradient(90deg, rgba(7, 28, 24, .94), rgba(7, 28, 24, .72) 48%, rgba(7, 28, 24, .2)),
        linear-gradient(180deg, rgba(7, 28, 24, .15), rgba(7, 28, 24, .65));
}

.hero-content {
    width: min(1180px, calc(100% - 48px));
    margin: 0 auto;
    padding-top: 60px;
    color: var(--white);
}

.hero-kicker {
    display: inline-block;
    margin-bottom: 20px;
    color: var(--orange-light);
    font-size: 13px;
    font-weight: 800;
    letter-spacing: .18em;
}

.hero-content h1 {
    max-width: 820px;
    margin: 0;
    font-size: clamp(54px, 7vw, 96px);
    line-height: .94;
    letter-spacing: -.055em;
    font-weight: 800;
}

.hero-content h1 em {
    color: var(--orange);
    font-style: normal;
}

.hero-content p {
    max-width: 600px;
    margin: 30px 0 34px;
    color: rgba(255, 255, 255, .8);
    font-size: 18px;
    line-height: 1.7;
}

.hero-btn,
.opening-btn,
.career-submit {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    border: 0;
    text-decoration: none;
    background: var(--orange);
    color: var(--white);
    padding: 15px 22px;
    font-size: 13px;
    font-weight: 800;
    cursor: pointer;
    transition: .25s ease;
}

.hero-btn:hover,
.opening-btn:hover,
.career-submit:hover {
    transform: translateY(-3px);
    background: #ff9e2e;
    box-shadow: 0 14px 30px rgba(248, 140, 24, .25);
}

.hero-scroll {
    position: absolute;
    right: 40px;
    bottom: 32px;
    display: flex;
    align-items: center;
    gap: 12px;
    color: rgba(255, 255, 255, .7);
    font-size: 13px;
    font-weight: 700;
    letter-spacing: .16em;
}

.hero-scroll i {
    color: var(--orange);
    font-size: 17px;
}

/* LIFE GALLERY */
.life-section {
    padding: 120px 0;
    background: var(--white);
}

.life-gallery {
    display: grid;
    grid-template-columns: 1.3fr .7fr .7fr;
    grid-auto-rows: 260px;
    gap: 16px;
    margin-top: 70px;
}

.gallery-item {
    position: relative;
    min-height: 260px;
    overflow: hidden;
    background: var(--green);
}

.gallery-item.gallery-large {
    grid-row: span 2;
}

.gallery-item.gallery-wide {
    grid-column: span 2;
}

.gallery-item img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
    transition: transform .6s ease;
}

.gallery-item:hover img {
    transform: scale(1.06);
}

.gallery-item::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, transparent 40%, rgba(7, 28, 24, .8));
}

.gallery-item span {
    position: absolute;
    z-index: 2;
    left: 20px;
    bottom: 18px;
    color: var(--white);
    font-size: 13px;
    font-weight: 800;
}

/* CORE VALUES */
.core-values-section {
    padding: 125px 0;
    background: radial-gradient(circle at 90% 10%, rgba(248, 140, 24, .08), transparent 28%), var(--green-soft);
}

.core-values-intro {
    max-width: 760px;
}

.core-values-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 22px;
    margin-top: 65px;
}

.core-value-card {
    overflow: hidden;
    background: var(--white);
    border: 1px solid var(--line);
    transition: transform .35s ease, box-shadow .35s ease;
}

.core-value-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 24px 55px rgba(7, 28, 24, .12);
}

.core-value-image {
    position: relative;
    height: 245px;
    overflow: hidden;
    background: var(--green-dark);
}

.core-value-image::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, transparent 20%, rgba(7, 28, 24, .5));
}

.core-value-image img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
    transition: transform .6s ease;
}

.core-value-card:hover .core-value-image img {
    transform: scale(1.06);
}

.core-value-number {
    position: absolute;
    z-index: 3;
    top: 18px;
    right: 18px;
    width: 42px;
    height: 42px;
    display: grid;
    place-items: center;
    background: var(--orange);
    color: var(--white);
    font-size: 13px;
    font-weight: 800;
}

.core-value-content {
    padding: 28px 28px 32px;
}

.core-value-icon,
.benefit-icon {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
    background: var(--green-light);
    color: var(--green);
    font-size: 20px;
    transition: .25s ease;
}

.core-value-card:hover .core-value-icon,
.benefit-card:hover .benefit-icon {
    background: var(--orange);
    color: var(--white);
}

.core-value-content h3 {
    margin: 0 0 12px;
    color: var(--green-dark);
    font-size: 24px;
    line-height: 1.2;
}

.core-value-content p {
    margin: 0;
    color: var(--muted);
    font-size: 14px;
    line-height: 1.75;
}

/* VIDEO */
.career-video-section {
    padding: 125px 0;
    background: var(--white);
}

.career-video-heading {
    margin-bottom: 55px;
}

.career-video {
    position: relative;
    width: 100%;
    overflow: hidden;
    background: #000;
    box-shadow: 0 30px 70px rgba(7, 28, 24, .18);
}

.career-video video {
    display: block;
    width: 100%;
    height: auto;
    max-height: 680px;
    background: #000;
    object-fit: contain;
}

.video-error {
    padding: 28px;
    color: var(--white);
    text-align: center;
    background: var(--green-deep);
}

.video-error>i {
    color: var(--orange);
    font-size: 30px;
}

.video-error h3 {
    margin: 12px 0;
}

.video-error p {
    color: rgba(255, 255, 255, .75);
    line-height: 1.7;
}

.video-error a {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: var(--orange-light);
    font-weight: 700;
}

/* BENEFITS */
.benefits-section {
    padding: 125px 0;
    background: var(--green-soft);
}

.benefits-heading,
.openings-heading {
    margin-bottom: 60px;
}

.benefits-grid {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 16px;
}

.benefit-card {
    position: relative;
    min-width: 0;
    min-height: 280px;
    padding: 28px 24px;
    background: var(--white);
    border: 1px solid var(--line);
    transition: .3s ease;
}

.benefit-card:hover {
    transform: translateY(-6px);
    border-color: rgba(248, 140, 24, .4);
    box-shadow: 0 20px 40px rgba(7, 28, 24, .1);
}

.benefit-number {
    position: absolute;
    top: 18px;
    right: 18px;
    color: #bdc9c4;
    font-size: 13px;
    font-weight: 800;
}

.benefit-icon {
    margin-bottom: 28px;
}

.benefit-card h3 {
    margin: 0 0 12px;
    color: var(--green-dark);
    font-size: 18px;
    line-height: 1.3;
}

.benefit-card p {
    margin: 0;
    color: var(--muted);
    font-size: 13px;
    line-height: 1.7;
}

/* OPENINGS */
.openings-section {
    padding: 125px 0;
    background: var(--white);
}

.openings-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.opening-card {
    display: grid;
    grid-template-columns: minmax(0, 1.7fr) minmax(200px, .8fr) auto;
    align-items: center;
    gap: 35px;
    padding: 32px;
    border: 1px solid var(--line);
    background: var(--white);
    transition: .3s ease;
}

.opening-card:hover {
    transform: translateX(5px);
    border-color: rgba(248, 140, 24, .4);
    box-shadow: 0 18px 40px rgba(7, 28, 24, .08);
}

.opening-type {
    display: inline-block;
    margin-bottom: 12px;
    color: var(--orange);
    font-size: 13px;
    font-weight: 800;
    letter-spacing: .14em;
}

.opening-info h3 {
    margin: 0 0 10px;
    color: var(--green-dark);
    font-size: 24px;
}

.opening-info p {
    max-width: 620px;
    margin: 0;
    color: var(--muted);
    font-size: 14px;
    line-height: 1.65;
}

.opening-meta {
    display: flex;
    flex-direction: column;
    gap: 12px;
    color: var(--muted);
    font-size: 13px;
}

.opening-meta span {
    display: flex;
    align-items: center;
    gap: 9px;
}

.opening-meta i {
    color: var(--orange);
}

/* APPLICATION */
.application-section {
    padding: 125px 0;
    background: radial-gradient(circle at 85% 20%, rgba(248, 140, 24, .12), transparent 30%), var(--green-deep);
}

.application-card {
    display: grid;
    grid-template-columns: .8fr 1.2fr;
    gap: 80px;
    padding: 70px;
    background: var(--white);
}

.application-copy h2 {
    font-size: clamp(44px, 5vw, 68px);
}

.application-copy h2 em {
    color: var(--orange);
}

.application-copy>p {
    max-width: 470px;
    color: var(--muted);
    font-size: 15px;
    line-height: 1.8;
}

.application-note {
    display: flex;
    align-items: flex-start;
    gap: 13px;
    max-width: 470px;
    margin-top: 35px;
    padding-top: 25px;
    border-top: 1px solid var(--line);
    color: var(--muted);
    font-size: 13px;
    line-height: 1.6;
}

.application-note i {
    flex-shrink: 0;
    color: var(--orange);
    font-size: 20px;
}

.career-form {
    display: flex;
    flex-direction: column;
    gap: 20px;
    min-width: 0;
}

.form-row {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 18px;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
}

.form-group label {
    color: var(--green-dark);
    font-size: 13px;
    font-weight: 800;
    letter-spacing: .05em;
}

.form-group input,
.form-group select,
.form-group textarea {
    width: 100%;
    min-width: 0;
    border: 1px solid var(--line);
    border-radius: 0;
    background: #fbfcfb;
    color: var(--ink);
    padding: 14px 15px;
    outline: none;
    font-family: inherit;
    font-size: 14px;
    transition: border-color .2s ease, box-shadow .2s ease;
}

.form-group textarea {
    resize: vertical;
    min-height: 120px;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
    border-color: var(--orange);
    box-shadow: 0 0 0 3px rgba(248, 140, 24, .1);
}

.resume-input {
    position: relative;
    display: flex;
    align-items: center;
    gap: 14px;
    min-height: 76px;
    padding: 15px;
    border: 1px dashed #cbd7d2;
    background: #fbfcfb;
}

.resume-input input {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
}

.resume-input>i {
    flex-shrink: 0;
    display: grid;
    place-items: center;
    width: 42px;
    height: 42px;
    background: var(--green-light);
    color: var(--green);
    font-size: 20px;
}

.resume-input span {
    display: flex;
    flex-direction: column;
    gap: 4px;
    overflow-wrap: anywhere;
    color: var(--green-dark);
    font-size: 13px;
    font-weight: 700;
}

.resume-input small {
    color: var(--muted);
    font-size: 13px;
    font-weight: 400;
}

.career-submit {
    width: fit-content;
    margin-top: 4px;
}

.form-message {
    margin: 0;
    color: var(--green);
    font-size: 13px;
    line-height: 1.6;
}

.form-disclaimer {
    margin: -10px 0 0;
    color: var(--muted);
    font-size: 13px;
    line-height: 1.6;
}

/* RESPONSIVE */
@media (max-width: 1100px) {
    .benefits-grid {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .core-values-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .opening-card {
        grid-template-columns: minmax(0, 1fr) auto;
    }

    .opening-meta {
        grid-column: 1;
        flex-direction: row;
        flex-wrap: wrap;
    }

    .opening-btn {
        grid-column: 2;
        grid-row: 1 / span 2;
    }

    .application-card {
        gap: 50px;
        padding: 50px;
    }
}

@media (max-width: 950px) {
    .careers-hero {
        min-height: 650px;
    }

    .life-section,
    .core-values-section,
    .career-video-section,
    .benefits-section,
    .openings-section,
    .application-section {
        padding: 95px 0;
    }

    .life-gallery {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        grid-auto-rows: 240px;
    }

    .gallery-item.gallery-large {
        grid-row: span 1;
    }

    .gallery-item.gallery-wide {
        grid-column: span 2;
    }

    .benefits-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .application-card {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 700px) {

    .section-wrap,
    .hero-content {
        width: calc(100% - 32px);
    }

    .careers-hero {
        min-height: 620px;
    }

    .hero-content h1 {
        font-size: clamp(48px, 12vw, 70px);
    }

    .hero-content p {
        font-size: 16px;
    }

    .hero-scroll {
        display: none;
    }

    .life-section,
    .core-values-section,
    .career-video-section,
    .benefits-section,
    .openings-section,
    .application-section {
        padding: 75px 0;
    }

    .section-heading h2,
    .core-values-intro h2,
    .career-video-heading h2,
    .application-copy h2 {
        font-size: clamp(40px, 10vw, 58px);
    }

    .life-gallery {
        grid-template-columns: 1fr;
        grid-auto-rows: 240px;
        margin-top: 45px;
    }

    .gallery-item.gallery-wide {
        grid-column: span 1;
    }

    .core-values-grid {
        grid-template-columns: 1fr;
        margin-top: 45px;
    }

    .core-value-image {
        height: 260px;
    }

    .benefits-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .opening-card {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 22px;
    }

    .opening-meta {
        width: 100%;
        flex-direction: column;
    }

    .opening-btn {
        width: 100%;
    }

    .application-card {
        padding: 32px 24px;
        gap: 45px;
    }

    .form-row {
        grid-template-columns: 1fr;
    }

    .career-submit {
        width: 100%;
    }
}

@media (max-width: 600px) {
    .benefits-grid {
        grid-template-columns: 1fr;
    }

    .benefit-card {
        min-height: auto;
    }

    .core-value-content {
        padding: 24px;
    }

    .career-video video {
        max-height: 420px;
    }
}

@media (max-width: 400px) {

    .section-wrap,
    .hero-content {
        width: calc(100% - 28px);
    }

    .careers-hero {
        min-height: 570px;
    }

    .hero-content h1 {
        font-size: 44px;
    }

    .hero-content p {
        font-size: 15px;
    }

    .hero-btn {
        width: 100%;
    }

    .application-card {
        padding: 28px 18px;
    }

    .core-value-image {
        height: 220px;
    }

    .benefit-card {
        padding: 24px 20px;
    }
}
</style>
